/** Call the year (screens §P6 Beat 1): the pure pieces between the three text boxes and the `commitForecast` body. */
import { useState } from "react";
import type { ContentCatalog, ForecastInput, ForecastStatus, TeamState } from "@bsim/contracts";
import { callRequiredIn, type Rate } from "@bsim/domain";
import { WHEN_PHRASE, forecastWhen, formatKpi } from "../../format";
import { labelOf } from "../../format/labels";
import { roleLabel } from "../../shell/TopBar";

/** The three called KPIs in display order (FORECAST_GATED_KPIS); labels as the sheet and the strip show them. */
export const FORECAST_FIELDS = [
  { kpi: "nim", label: `${labelOf("nim")}, ${WHEN_PHRASE[forecastWhen("nim").when]}` },
  { kpi: "ncoRate", label: `${labelOf("ncoRate")}, ${WHEN_PHRASE[forecastWhen("ncoRate").when]}` },
  { kpi: "cet1Marked", label: `${labelOf("cet1Marked")}, ${WHEN_PHRASE[forecastWhen("cet1Marked").when]}` },
] as const;

/** Each field starts at last year's figure on the team's own bank (OR-30.29, OR-30.90), typed the way the box reads it ("3.54", no percent sign): the player changes it only where a move is expected. Empty when the status has no baseline (a band call). */
export function lastYearTexts(status: ForecastStatus | null): { readonly nim: string; readonly ncoRate: string; readonly cet1Marked: string } {
  const text = (kpi: (typeof FORECAST_FIELDS)[number]["kpi"]): string => {
    const v = status?.noChangeBaseline[kpi];
    return v === undefined || status?.format !== "numeric" ? "" : formatKpi(kpi, v).replace("%", "");
  };
  return { nim: text("nim"), ncoRate: text("ncoRate"), cet1Marked: text("cet1Marked") };
}

/** The forecast gate (ADR-0040): the team's call is in, or the round has none (round 1), so the gated preview values and event facts are open. */
export const forecastGateOpen = (team: TeamState): boolean => team.forecast?.state === "committed" || !callRequiredIn(team.round?.roundNo ?? 0);

/** "3.49" or "3.49%" → 0.0349; null for anything that is not a plain number. The string exponent keeps the decimal exact. */
export function parsePercent(text: string): Rate | null {
  const match = /^(-?\d+(?:\.\d+)?)\s*%?$/.exec(text.trim());
  return match?.[1] === undefined ? null : (Number(`${match[1]}e-2`) as Rate);
}

export const numericForecastBody = (nim: Rate, ncoRate: Rate, cet1Marked: Rate): Extract<ForecastInput, { format: "numeric" }> =>
  ({ format: "numeric", nim, ncoRate, cet1Marked });

/**
 * What the no-change plan does with capital, from the line the no-change plan itself plays (`ForecastStatus.noChangeBuybackCet1TargetRate`: the standing policy's buyback line, never the
 * player's own floor), named as the policy's: "it keeps returning capital above 10.50% (your standing policy's line)", or "it does not buy back shares" when the line is off. A status
 * without the field reads the draft's round-open setting, else the pack's default line.
 */
export function standingBuybackLine(team: TeamState, catalog: ContentCatalog): string {
  const line = team.forecast?.noChangeBuybackCet1TargetRate !== undefined ? team.forecast.noChangeBuybackCet1TargetRate
    : team.draft === null ? catalog.cet1TargetRate : team.draft.seedDecisions.capital.buyback.cet1TargetRate;
  return line === null ? "it does not buy back shares" : `it keeps returning capital above ${formatKpi("cet1Marked", line)} (your standing policy\u2019s line)`;
}

/** Who called it: the member's display name, else the role label. `status` defaults to the team's own (a fresh 201 or reload passes its own). */
export function committerName(team: TeamState, status: ForecastStatus | null = team.forecast): string | null {
  const by = status?.committedBy;
  if (by === null || by === undefined) return null;
  return team.members.find((m) => m.userId === by.userId)?.displayName ?? roleLabel(by.role);
}


/**
 * State that survives a reload before the call is committed: the picks and typed values of a call not yet made, kept in this browser per round.
 * A per-viewer convenience: without storage, or once the call is committed, it is simply the initial value.
 */
export function usePersistedCall<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => { try { const stored = localStorage.getItem(key); return stored === null ? initial : { ...initial, ...(JSON.parse(stored) as T) }; } catch { return initial; } });
  return [value, (next) => { setValue(next); try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* per-viewer convenience only */ } }];
}
